import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '@/lib/use-sidebar';
import { cx } from '@/lib/cx';
import { Button } from './Button';

/*
 * Width only, and every entry is `sm:`-prefixed on purpose: below `sm` the
 * panel is `w-full` and none of these apply, so a dialog is a full-bleed sheet
 * at every size. That is what makes `xl` safe on a phone — a wide dialog is not
 * a wide box with its sides cut off, it is the same sheet the others are.
 *
 * `xl` exists for the single-view quote/invoice forms: a header field grid
 * three-to-four columns across plus an inline line-item table roughly ten
 * columns wide, which `lg` (48rem) cannot seat. 80rem is the widest step that
 * still leaves a margin on a 1440px display; past that the header grid reads as
 * a scattering of fields rather than a form. It never exceeds the viewport,
 * because `w-full` inside the `sm:p-4` container is the real ceiling and
 * `max-w` only ever lowers it.
 */
const SIZES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-xl',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-7xl',
} as const;

export type ModalSize = keyof typeof SIZES;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Rendered in the labelling `h2`; a node (e.g. a record `<Link>`) is allowed. */
  title: ReactNode;
  description?: ReactNode;
  /**
   * Widths from `sm` up; below that every size is a full-bleed sheet. `xl` is
   * for a wide, dense single-view form — reach for it when the content is a
   * table, not to give a column of fields more room.
   */
  size?: ModalSize;
  /**
   * Footer actions. Kept a slot rather than a fixed confirm/cancel pair so a
   * form can own its own submit button and disabled state.
   */
  footer?: ReactNode;
  /**
   * The form's own actions, rendered in the HEADER beside the title.
   *
   * 🔴 **FOR A LONG SCROLLING FORM, AND IT IS AN ALTERNATIVE TO `footer` RATHER
   * THAN AN ADDITION TO IT — owner's decision, 2026-09-14.** The three document
   * forms carry a line-items grid with a Save of its own partway down, so the
   * document's Cancel and Save moved up here where the reader is already
   * looking; passing the same pair to both slots would put two identical Saves
   * on one dialog, which reads as two different saves rather than one action.
   *
   * ⚠️ **The footer is NOT broken and was never scrolling away** — the panel is
   * a flex column whose BODY scrolls, so both ends stay pinned. This is about
   * where the eye is on a form a screen tall, not about reachability.
   *
   * ⚠️ **It wraps to its own row below `sm`**, because a long document title
   * (`Edit <company> - INV-1040`) beside two buttons and the close × has
   * nowhere to go at 320px, and a header that compresses the title to one word
   * per line is worse than one that is two rows tall.
   */
  headerActions?: ReactNode;
  /**
   * Set false when losing input would cost the user work — a dirty form should
   * not vanish because someone clipped the backdrop. Escape still closes.
   */
  dismissOnBackdrop?: boolean;
  /** Where focus lands on open. Defaults to the first focusable element. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  children: ReactNode;
}

/**
 * An accessible modal dialog, portalled to the body so it can never be clipped
 * by the shell's stacking or overflow contexts.
 *
 * Renders nothing when closed: an off-screen dialog left mounted keeps its
 * links in the tab order and its content in the accessibility tree.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  headerActions,
  dismissOnBackdrop = true,
  initialFocusRef,
  children,
}: ModalProps) {
  const titleId = useId();
  const descId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  useBodyScrollLock(open);

  // Remember what had focus before opening, and hand it back on close —
  // otherwise focus falls to the top of the document.
  useEffect(() => {
    if (!open) return;
    returnFocusTo.current = document.activeElement as HTMLElement | null;
    const target =
      initialFocusRef?.current ?? panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus();
    return () => returnFocusTo.current?.focus();
  }, [open, initialFocusRef]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;

      /*
       * Same reason Escape stops here: a modal opened from inside another one
       * is a React-tree descendant of it, so an un-stopped Tab would run both
       * focus traps and the outer one would pull focus out of the inner dialog.
       */
      event.stopPropagation();

      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      const first = items.at(0);
      const last = items.at(-1);
      if (!first || !last) return;

      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panelRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div
        aria-hidden="true"
        onClick={dismissOnBackdrop ? onClose : undefined}
        className="absolute inset-0 bg-scrim backdrop-blur-[2px]"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        onKeyDown={onKeyDown}
        className={cx(
          // Full-height sheet on a phone, centred dialog from `sm` up.
          'relative flex max-h-[92svh] w-full flex-col rounded-t-xl border border-border bg-surface shadow-lg',
          'sm:max-h-[86svh] sm:rounded-xl',
          SIZES[size],
        )}
      >
        <header className="flex flex-wrap items-start gap-x-4 gap-y-2 border-b border-border px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-sm font-semibold text-fg">
              {title}
            </h2>
            {description && (
              <p id={descId} className="mt-0.5 text-xs text-fg-muted">
                {description}
              </p>
            )}
          </div>
          {/*
           * ⚠️ **`order-last w-full` BELOW `sm`, INLINE FROM `sm` UP.** Full
           * width cannot share a row, so the actions drop beneath the title and
           * the close × keeps the top-right corner it has on every other dialog
           * in the app; from `sm` the order is reset and they sit inline, before
           * the ×. Without the full-width rung a long title is compressed to a
           * word a line at 320px.
           */}
          {headerActions && (
            <div className="order-last flex w-full min-w-0 flex-wrap items-center gap-2 sm:order-none sm:w-auto sm:max-w-[60%] sm:justify-end">
              {headerActions}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label="Close dialog"
            leadingIcon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                aria-hidden="true"
                className="h-4 w-4"
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            }
          />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>

        {footer && (
          <footer className="flex flex-col-reverse gap-2 border-t border-border px-4 py-3 sm:flex-row sm:justify-end">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
